import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { StickyNote } from "lucide-react";
import { Badge } from "components/ui/badge";
import Avatar from "components/shared/Avatar";
import Tooltip from "components/shared/Tooltip";
import { getPrimaryActiveResource, renderUserName } from "utils/helpers/user";
import { applyTimezone } from "utils/helpers/date";
import { useUser } from "utils/hooks/user";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { STATUS_CLASSNAMES } from "../hooks/useColumns";
import { cn } from "lib/utils";

const getStatusClassName = (status: string) =>
    STATUS_CLASSNAMES[status] ?? STATUS_CLASSNAMES[status?.split("-")[0]];

function StatusBadge({ status }: { status: string }) {
    const { t } = useTranslation();
    return (
        <Badge variant="outline" className={cn("tw-whitespace-nowrap", getStatusClassName(status))}>
            {t(status)}
        </Badge>
    );
}

function LogRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <span className="tw-font-semibold">{label}:</span>
            {children}
        </div>
    );
}

function StatusCell({ request }: { request: any }) {
    const { t } = useTranslation();
    const user = useUser();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const logs: any[] = request?.logs || [];

    const location = useMemo(
        () => getPrimaryActiveResource(user?.locations || []),
        [user?.locations],
    );

    if (!request?.status) return <>—</>;

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <StatusBadge status={request.status} />

            {logs.length > 0 && (
                <Tooltip
                    trigger={
                        <span className="tw-inline-flex tw-cursor-pointer tw-p-2 tw-text-primary">
                            <StickyNote className="tw-h-4 tw-w-4" />
                        </span>
                    }
                    side="left"
                    align="center"
                    contentClassName="tw-bg-background tw-text-foreground tw-shadow-md tw-p-2 tw-max-w-lg tw-max-h-96 tw-overflow-y-auto"
                >
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        {logs.map((log, index) => {
                            const date = log?.date && applyTimezone(moment(log.date), location?.timezoneValue, true);
                            return (
                                <div key={index} className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-dashed tw-p-3">
                                    <Avatar user={log?.user} />

                                    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
                                        {log?.user && (
                                            <LogRow label={t("user")}>
                                                <span>{renderUserName(log.user)}</span>
                                            </LogRow>
                                        )}
                                        {date && (
                                            <LogRow label={t("date")}>
                                                <span>{moment(date).format(`${dateFormat} ${timeFormat}`)}</span>
                                            </LogRow>
                                        )}
                                        {log?.status && (
                                            <LogRow label={t("status")}>
                                                <StatusBadge status={log.status} />
                                            </LogRow>
                                        )}
                                        {log?.reason && (
                                            <LogRow label={t("reason")}>
                                                <span className="tw-whitespace-pre-wrap tw-break-words">{log.reason}</span>
                                            </LogRow>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Tooltip>
            )}
        </div>
    );
}

export default StatusCell;
